import React, { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import './JamefConfigModal.css';

function authHeaders(token) {
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
}

export default function JamefConfigModal({ open, onClose, apiBaseUrl, onSaved }) {
  const [usuario, setUsuario] = useState('');
  const [senha, setSenha] = useState('');
  const [senhaCadastrada, setSenhaCadastrada] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [justSaved, setJustSaved] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [testMsg, setTestMsg] = useState(null);
  const [testOk, setTestOk] = useState(null);

  const load = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setLoading(true);
    setSaveError('');
    try {
      const res = await fetch(`${apiBaseUrl}/api/integracoes/jamef`, {
        headers: authHeaders(token)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        setSaveError(json.message || 'Erro ao carregar dados.');
        return;
      }
      const d = json.data || {};
      setUsuario(String(d.usuario || ''));
      setSenha('');
      setSenhaCadastrada(!!d.senhaCadastrada);
      setTestMsg(null);
      setTestOk(null);
    } catch (e) {
      setSaveError(e.message || 'Erro de rede.');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    if (!open) return;
    setJustSaved(false);
    load();
  }, [open, load]);

  const handleSave = async () => {
    if (!String(usuario || '').trim()) {
      setSaveError('Usuário é obrigatório.');
      return;
    }
    if (!String(senha || '').trim() && !senhaCadastrada) {
      setSaveError('Senha é obrigatória no primeiro cadastro.');
      return;
    }
    setSaveError('');
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setJustSaved(false);
    setLoading(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/integracoes/jamef`, {
        method: 'PUT',
        headers: authHeaders(token),
        body: JSON.stringify({ usuario: usuario.trim(), senha })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        setSaveError(json.message || 'Erro ao salvar.');
        return;
      }
      await load();
      setJustSaved(true);
      if (typeof onSaved === 'function') onSaved();
    } catch (e) {
      setSaveError(e.message || 'Erro de rede.');
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setTestMsg(null);
    setTestOk(null);
    setTestLoading(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/integracoes/jamef/test`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({})
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.success) {
        setTestOk(true);
        setTestMsg('Sucesso no teste.');
        return;
      }
      setTestOk(false);
      setTestMsg(json.message || `Erro HTTP ${res.status}.`);
    } catch (e) {
      setTestOk(false);
      setTestMsg(e.message || 'Erro de rede.');
    } finally {
      setTestLoading(false);
    }
  };

  if (!open) return null;

  const canTest = senhaCadastrada && String(usuario || '').trim().length > 0;

  return (
    <div className="jamef-modal-overlay">
      <div
        className="jamef-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="jamef-modal-title"
      >
        <div className="jamef-modal-header">
          <div>
            <h2 id="jamef-modal-title" className="jamef-modal-title">
              Jamef
            </h2>
            <p className="jamef-modal-sub">Rastreio de encomendas — uma credencial por cliente (usuário e senha).</p>
          </div>
          <button type="button" className="jamef-modal-close" onClick={onClose} aria-label="Fechar">
            <X size={20} />
          </button>
        </div>
        <div className="jamef-modal-body">
          {justSaved && <div className="jamef-test-msg success">Dados salvos com sucesso.</div>}
          {saveError && (
            <div className="jamef-test-msg error" style={{ marginBottom: 12 }}>
              {saveError}
            </div>
          )}

          <div className="jamef-field">
            <label htmlFor="jamef-usuario">Usuário</label>
            <input
              id="jamef-usuario"
              type="text"
              autoComplete="username"
              value={usuario}
              onChange={(e) => setUsuario(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="jamef-field">
            <label htmlFor="jamef-senha">Senha</label>
            {senhaCadastrada && !senha ? (
              <div className="jamef-senha-maskline" aria-hidden>
                ••••••••
              </div>
            ) : null}
            <input
              id="jamef-senha"
              type="password"
              autoComplete="new-password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              placeholder={senhaCadastrada ? 'Deixe em branco para manter a senha atual' : ''}
              disabled={loading}
            />
          </div>

          <div className="jamef-actions">
            <button
              type="button"
              className="jamef-btn jamef-btn-primary"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? 'Aguarde…' : 'Salvar'}
            </button>
          </div>

          {canTest && (
            <div className="jamef-test-block">
              <h3 className="jamef-test-title">Testar integração</h3>
              <p className="jamef-test-hint">Usa as credenciais salvas para autenticar na API Jamef.</p>
              <div className="jamef-test-row">
                <button
                  type="button"
                  className="jamef-btn-test"
                  onClick={handleTest}
                  disabled={testLoading}
                >
                  Testar
                </button>
              </div>
              {testLoading && (
                <div className="jamef-test-spinner-wrap" aria-live="polite">
                  <div className="jamef-test-spinner" />
                </div>
              )}
              {!testLoading && testMsg && (
                <div className={`jamef-test-msg ${testOk ? 'success' : 'error'}`}>{testMsg}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
